import React, { useState } from 'react';
import { Share2, MousePointer, PieChart, DollarSign, Users } from 'lucide-react';

/**
 * Компонент для выбора и настройки рекламных платформ
 */
const PlatformsSection = ({ 
  formData, 
  handlePlatformChange, 
  errors, 
  platformTypes 
}) => {
  const [activePlatformTab, setActivePlatformTab] = useState('settings');

  // Проверка, включена ли хотя бы одна платформа
  const anyPlatformEnabled = Object.values(formData.platforms).some(platform => platform.enabled);
  
  // Расчет суммы процентов бюджета для включенных платформ
  const totalBudgetPercent = Object.values(formData.platforms)
    .filter(platform => platform.enabled)
    .reduce((sum, platform) => sum + parseFloat(platform.budget || 0), 0);

  // Получение иконки для платформы
  const getPlatformIcon = (iconName) => {
    switch (iconName) {
      case 'Search': return <MousePointer className="w-5 h-5" />;
      case 'Users': return <Users className="w-5 h-5" />;
      case 'Image': return <Share2 className="w-5 h-5" />;
      case 'Video': return <Share2 className="w-5 h-5" />;
      case 'Mail': return <Share2 className="w-5 h-5" />;
      default: return <Share2 className="w-5 h-5" />;
    }
  };

  return (
    <div className="mb-8 bg-indigo-50 dark:bg-indigo-900/20 p-6 rounded-lg border border-indigo-200 dark:border-indigo-800">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Share2 className="w-5 h-5 mr-2 text-indigo-500" />
        Рекламные платформы
      </h3>

      {/* Ошибки с платформами */}
      {errors.platforms && (
        <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
          {errors.platforms}
        </div>
      )}
      
      {errors.platformBudgets && (
        <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
          {errors.platformBudgets} (текущая сумма: {totalBudgetPercent.toFixed(1)}%)
        </div>
      )}

      {/* Вкладки платформ */}
      <div className="mb-4 flex space-x-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActivePlatformTab('settings')}
          className={`pb-2 px-4 text-sm font-medium ${
            activePlatformTab === 'settings'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Настройки
        </button>
        <button
          onClick={() => setActivePlatformTab('performance')}
          className={`pb-2 px-4 text-sm font-medium ${
            activePlatformTab === 'performance'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Показатели
        </button>
      </div>

      {/* Контент вкладок */}
      {activePlatformTab === 'settings' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {platformTypes.map((platform) => {
            const platformData = formData.platforms[platform.id];
            
            return (
              <div 
                key={platform.id} 
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    {getPlatformIcon(platform.iconName)}
                    <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                      {platform.name}
                    </span>
                  </div>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={platformData.enabled}
                      onChange={(e) => handlePlatformChange(platform.id, 'enabled', e.target.checked)}
                      className="form-checkbox"
                    />
                  </label>
                </div>
                
                {platformData.enabled && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Доля бюджета (%)
                      </label>
                      <input
                        type="text"
                        value={platformData.budget}
                        onChange={(e) => handlePlatformChange(platform.id, 'budget', e.target.value)}
                        className={`form-input w-full ${
                          errors[`${platform.id}_budget`] ? 'input-error' : ''
                        }`}
                        placeholder="Процент бюджета"
                      />
                      {errors[`${platform.id}_budget`] && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {errors[`${platform.id}_budget`]}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Стоимость клика (CPC)
                      </label>
                      <input
                        type="text"
                        value={platformData.cpc}
                        onChange={(e) => handlePlatformChange(platform.id, 'cpc', e.target.value)}
                        className={`form-input w-full ${
                          errors[`${platform.id}_cpc`] ? 'input-error' : ''
                        }`}
                        placeholder="Стоимость клика"
                      />
                      {errors[`${platform.id}_cpc`] && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {errors[`${platform.id}_cpc`]}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        CTR (%)
                      </label>
                      <input
                        type="text"
                        value={platformData.ctr}
                        onChange={(e) => handlePlatformChange(platform.id, 'ctr', e.target.value)}
                        className={`form-input w-full ${
                          errors[`${platform.id}_ctr`] ? 'input-error' : ''
                        }`}
                        placeholder="Кликабельность"
                      />
                      {errors[`${platform.id}_ctr`] && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {errors[`${platform.id}_ctr`]}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Конверсия (%)
                      </label>
                      <input
                        type="text"
                        value={platformData.conversionRate}
                        onChange={(e) => handlePlatformChange(platform.id, 'conversionRate', e.target.value)}
                        className={`form-input w-full ${
                          errors[`${platform.id}_conversionRate`] ? 'input-error' : ''
                        }`}
                        placeholder="Процент конверсии"
                      />
                      {errors[`${platform.id}_conversionRate`] && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {errors[`${platform.id}_conversionRate`]}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Вкладка показателей */}
      {activePlatformTab === 'performance' && (
        <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Рекомендации по настройке платформ
          </h4>
          <div className="space-y-3">
            {platformTypes.map((platform) => (
              <div 
                key={platform.id} 
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center">
                  {getPlatformIcon(platform.iconName)}
                  <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                    {platform.name}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Рекомендуемый CPC: {platform.defaultCpc} ₽
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformsSection;